import { LitElement, html, css} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import {repeat} from 'lit/directives/repeat.js';
import '../../voyage-popup/src/voyage-popup';
import '../../voy-timeinput/src/voy-timeinput';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { TerminalInfo } from '../../../src/models/types/terminals-model.types';

@customElement('voy-port-search')
export class VoyPortSearch extends LitElement
{
    @property({type: Object}) terminalData: TerminalInfo[] = [];
    @property({type: Object}) fixedPorts: TerminalInfo[] = [];
    
    @property({attribute: false}) private _enabled: boolean = false;
    @property({attribute: false}) private _fixedList: TerminalInfo[] = [];
    @property({attribute: false}) private _shownList: TerminalInfo[] = [];
    @property({attribute: false}) private _selectedPort: TerminalInfo | null = null;

    constructor() {
        super();
        this._searchItemClicked = this._searchItemClicked.bind(this);
    }

    static get styles()
    {
        return [
            css`

            .container {
                font-family: MaerskText;
                font-size: 14px;
                color: #353535;
            }

            .display {
                width: 210px;
                height: 50px;
                background: #FFFFFF;
                border: 1px solid #CFCFCF;
                border-radius: 2.5px;
                display: grid;
                grid-template-columns: auto auto;
                place-content: start space-between;
                cursor: pointer;
            }

            .displayed-port {
                padding: 12px;
                text-align: left;
                user-select: none;
            }

            .dropdown-svg {
                padding: 12px;
                cursor: pointer;
            }

            .dropdown-svg > svg{
                color: var(--neutrals-grey-04, black);
                width: 9px;
                height: 14px;
                rotate: 90;
            }

            .dropdown-content {
                background: #FFFFFF;
                border: 1px solid #CFCFCF;
                border-radius: 2.5px;
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
                width: 210px;
                height: 386px;
                display: block;
                position: absolute;
                background: #FFFFFF;
                min-width: 210px;
                overflow: auto;
                z-index: 4;
            }

            .dropdown-content[hidden] {
                display: none;
            }

            .search-result {
                cursor: pointer;
                margin: 12px;
                text-align: left;
                user-select: none;
            }

            .search-result:hover {
                background: rgba(0, 84, 135, 0.1);
            }

            .search-input {
                background: #FFFFFF;
                border: 1px solid #CFCFCF;
                border-radius: 2.5px;
                width: 176px;
                height: 51px;
                margin-top: 15px;
                margin-bottom: 15px;
            }

            .divider {
                border-bottom: 1px solid var(--neutrals-grey-06);
                height: 2px;
            }

            .search-box-container {
                display: grid;
                place-items: center;
            }
        `
        ];
    }

    render()
    {
        return html`
        <div class="container">
            <div class="display" @click=${this._onDisplayClicked}>
                <div class="displayed-port">${this._selectedPort !== null ? html`${this._selectedPort!.city}` : html``}</div>
                <div class="dropdown-svg">${this._enabled ? html`${SvgIcons.lightChevronUp}` : html`${SvgIcons.lightChevronDown}`}</div>
            </div>
            <div id="dropdown" class="dropdown-content" ?hidden=${!this._enabled}>
                <div class="search-box-container">
                    <input type="text" placeholder="Search.." class="search-input" @input=${this.onSearchInput}>
                </div>
                <div class="search-results-display">
                    ${repeat(this._fixedList, i => i.locationId, (item, index) => html`
                        <div class="search-result" @click=${() => this._searchItemClicked(item)}>${item.city}</div>
                    `)
                    }
                    <div class="divider"></div>
                    ${repeat(this._shownList, i => i.locationId, (item, index) => html`
                        <div class="search-result" @click=${() => this._searchItemClicked(item)}>${item.city}</div>
                    `)
                    }
                </div>
            </div>
        </div>
        
        `;
    }

    private async _searchItemClicked(item: TerminalInfo) {
        this._selectedPort = item;
        
        if (this._fixedList.filter(ft => ft.city === item.city).length === 0) {
            this._addToCircularBuffer(item);
        }

        this._onDisplayClicked();
        this.dispatchEvent(new CustomEvent('port-selected', { detail: item }));
    }

    private _addToCircularBuffer(item: TerminalInfo) {
        this._fixedList.splice(2, 0, item);
        this._fixedList = this._fixedList.slice(0, 5); // limited to 5 items
    }

    private _onDisplayClicked() {
        this._enabled = !this._enabled;
    }

    onSearchInput(evt: { target: { value: string; }; }) {
        const filterText = evt.target.value;
        this._applyFilter(filterText);
    }

    firstUpdated() {
        this._applyFilter("");
    }

    _applyFilter(filterText: string) {
        const uniqueCities : TerminalInfo[] = []
        const tempCities: Set<string> = new Set();

        this.terminalData.forEach(t => {
            if (!tempCities.has(t.city) && this._fixedList.filter(ft => ft.city === t.city).length === 0) {
                uniqueCities.push(t);
                tempCities.add(t.city);
            }
        })
        
        let shownList = uniqueCities;
        
        shownList = shownList.filter(item => {
            if (!item.city) return false;
            return item.city.toLocaleLowerCase().includes(filterText.toLocaleLowerCase())
        });

        this._shownList = shownList.sort((a, b) => {
            var nameA = a.city.toUpperCase();
            var nameB = b.city.toUpperCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
          
            // names must be equal
            return 0;
          });
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has("fixedPorts")) {
            if (this.fixedPorts !== null && this.fixedPorts.length > 0) {
                this._selectedPort = this.fixedPorts[0];
            }

            this._fixedList = this.fixedPorts;
        }

        super.updated(changedProperties);
    }
};
